import {
  IPurchaseWalletCreditRequest,
  IUpdateWalletAutoRechargeRequest,
  IWalletResponse,
  ISmsUsageInvoicesResponse,
  ISmsUsageInvoiceResponseItem,
  ISubscriptionResponse,
  IUpdateSubscriptionRequest,
  IUpdateSubscriptionResponse,
  SubscriptionCancelReason,
} from 'shared';

import { CacheTags } from '../../types/cacheTags';
import { emptySplitApi } from '../baseApi';

export interface UpdateSubscriptionPayload {
  id: number;
  update: IUpdateSubscriptionRequest;
}

export const subscriptionsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchSubscriptions: builder.query<ISubscriptionResponse, void>({
      query: () => ({
        url: `subscription`,
        method: `GET`,
      }),
      providesTags: result => result?.subscriptions ?
        [
          ...result.subscriptions.map(({ id }) => ({ type: CacheTags.Subscriptions, id })),
          { type: CacheTags.Subscriptions, id: `LIST` },
        ]
        : [{ type: CacheTags.Subscriptions, id: `LIST` }],
    }),
    actionSubscription: builder.mutation({
      query: ({ id, action, organisationId }) => ({
        url: `subscription/${id}/${action}`,
        method: `POST`,
        params: {
          org: organisationId,
        },
        headers: {
          [`X-Organisation-Id`]: organisationId,
        },
      }),
      invalidatesTags: ({ id }) => [
        { type: CacheTags.Subscriptions, id },
        { type: CacheTags.Organisations, id: `LIST` },
      ],
    }),
    createSubscription: builder.mutation({
      query: takeoverForOrgId => ({
        url: `subscription`,
        method: `POST`,
        data: {
          takeoverForOrgId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Subscriptions, id: `LIST` }],
    }),
    deleteSubscription: builder.mutation({
      query: ({ id }) => ({
        url: `subscription/${id}`,
        method: `DELETE`,
      }),
      invalidatesTags: [{ type: CacheTags.Subscriptions, id: `LIST` }],
    }),
    updateSubscription: builder.mutation<IUpdateSubscriptionResponse, UpdateSubscriptionPayload>({
      query: ({ id, ...body }) => ({
        url: `subscription/${id}`,
        method: `PATCH`,
        data: body.update,
      }),
      invalidatesTags: ({ id }) => [
        { type: CacheTags.Subscriptions, id },
        { type: CacheTags.Organisations, id: `LIST` },
      ],
    }),
    changePlan: builder.mutation({
      query: ({ subscriptionId, lookupKey }) => ({
        url: `subscription/${subscriptionId}/change-plan`,
        method: `POST`,
        data: {
          lookupKey,
        },
      }),
      invalidatesTags: ({ id }) => [
        { type: CacheTags.Subscriptions, id },
        { type: CacheTags.Organisations, id: `LIST` },
      ],
    }),
    cancelSubscription: builder.mutation<void, { id: number; reason: SubscriptionCancelReason; comment?: string }>({
      query: ({ id, reason, comment }) => ({
        url: `subscription/${id}/cancel`,
        method: `POST`,
        data: {
          reason,
          comment,
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: CacheTags.Subscriptions, id },
        { type: CacheTags.Organisations, id: `LIST` },
      ],
    }),
    addOnOrg: builder.mutation({
      query: ({ subscriptionId }) => ({
        url: `subscription/${subscriptionId}/add-org`,
        method: `POST`,
      }),
      invalidatesTags: ({ id }) => [
        { type: CacheTags.Subscriptions, id },
        { type: CacheTags.Organisations, id: `LIST` },
      ],
    }),
    fetchSmsUsageInvoices: builder.query<ISmsUsageInvoiceResponseItem[], { subscriptionId: number }>({
      query: ({ subscriptionId }) => ({
        url: `subscription/${subscriptionId}/sms-usage-invoices`,
        method: `GET`,
      }),
      transformResponse: (result: ISmsUsageInvoicesResponse) => result.invoices,
    }),
    fetchWallet: builder.query<IWalletResponse, { subscriptionId: number }>({
      query: ({ subscriptionId }) => ({
        url: `subscription/${subscriptionId}/wallet`,
        method: `GET`,
      }),
      providesTags: [CacheTags.Wallet],
    }),
    updateWalletAutoRecharge: builder.mutation<void, { subscriptionId: number; update: IUpdateWalletAutoRechargeRequest }>({
      query: ({ subscriptionId, update }) => ({
        url: `subscription/${subscriptionId}/wallet/auto-recharge`,
        method: `PUT`,
        data: update,
      }),
      invalidatesTags: [CacheTags.Wallet],
    }),
    purchaseWalletCredit: builder.mutation<void, { subscriptionId: number } & IPurchaseWalletCreditRequest>({
      query: ({ subscriptionId, ...data }) => ({
        url: `subscription/${subscriptionId}/wallet/purchase`,
        method: `POST`,
        data,
      }),
      invalidatesTags: [CacheTags.Wallet],
    }),
  }),
});

export const {
  useFetchSubscriptionsQuery,
  useActionSubscriptionMutation,
  useCreateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useChangePlanMutation,
  useAddOnOrgMutation,
  useCancelSubscriptionMutation,
  useLazyFetchSmsUsageInvoicesQuery,
  useFetchWalletQuery,
  useUpdateWalletAutoRechargeMutation,
  usePurchaseWalletCreditMutation,
} = subscriptionsApi;
